import {ChevronDown} from "lucide-react";
import {useState} from "react";

import {Badge} from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {cn} from "@/lib/utils";

import type {SafelistingCoverageFromDays} from "../lib/compute-safelisting-coverage-from-days";
import {
  getSafelistingDayStatusBadgeVariant,
  getSafelistingDayStatusLabel,
} from "../lib/safelisting-quota-display";

const DETAIL_COLLAPSE_THRESHOLD = 4;

type SafelistingCoverageSummaryProps = {
  coverage: SafelistingCoverageFromDays;
  className?: string;
};

function CoverageDayStatusBadge({status}: {status: SafelistingCoverageFromDays["rows"][number]["status"]}) {
  if (status !== "cancelled" && status !== "skipped") return null;

  return (
    <Badge
      variant={getSafelistingDayStatusBadgeVariant(status)}
      className="shrink-0 px-1.5 py-0 text-[10px]"
    >
      {getSafelistingDayStatusLabel(status)}
    </Badge>
  );
}

function NightList({coverage}: {coverage: SafelistingCoverageFromDays}) {
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0" aria-label="Coverage by date">
      {coverage.rows.map((row) => {
        const isCancelled = row.status === "cancelled";
        const isSkipped = row.status === "skipped";
        const isMuted = isCancelled || isSkipped;

        return (
          <li
            key={row.anchorDate}
            className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 gap-y-0 text-sm leading-snug tabular-nums"
          >
            {row.humanLabel ? (
              <span
                className={cn(
                  "text-foreground font-semibold",
                  isCancelled && "text-muted-foreground line-through",
                )}
              >
                {row.humanLabel}
              </span>
            ) : (
              <span
                className={cn(
                  "text-foreground min-w-[3.25rem] font-medium",
                  isMuted && "text-muted-foreground",
                  isCancelled && "line-through",
                )}
              >
                {row.compactDateLabel}
              </span>
            )}
            <span className={cn(isCancelled && "line-through")}>{row.compactTimeLabel}</span>
            <CoverageDayStatusBadge status={row.status} />
          </li>
        );
      })}
    </ul>
  );
}

function coverageHeaderSuffix(coverage: SafelistingCoverageFromDays): string {
  const parts: string[] = [];
  if (coverage.cancelledCount > 0) {
    parts.push(
      `${coverage.cancelledCount} cancelled`,
    );
  }
  if (coverage.skippedCount > 0) {
    parts.push(`${coverage.skippedCount} skipped`);
  }
  return parts.length > 0 ? ` · ${parts.join(" · ")}` : "";
}

export function SafelistingCoverageSummary({
  coverage,
  className,
}: SafelistingCoverageSummaryProps) {
  const [detailsOpen, setDetailsOpen] = useState(
    coverage.rows.length < DETAIL_COLLAPSE_THRESHOLD,
  );
  const showCollapsibleDetails = coverage.rows.length >= DETAIL_COLLAPSE_THRESHOLD;

  return (
    <div
      className={cn(
        "border-border bg-muted/30 flex flex-col gap-2.5 rounded-lg border px-3 py-2.5",
        className,
      )}
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-muted-foreground text-xs font-medium">
          Coverage · {coverage.countLabel}
          {coverageHeaderSuffix(coverage)}
        </span>
        <p className="text-foreground text-sm leading-snug tabular-nums">
          {coverage.overallRangeLabel}
        </p>
      </div>

      <div className="flex flex-col gap-1">
        {showCollapsibleDetails ? (
          <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
            <CollapsibleTrigger
              type="button"
              className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between gap-2 text-left text-xs font-medium"
            >
              <span>
                Each {coverage.dateWord} ({coverage.rows.length})
              </span>
              <ChevronDown
                className={cn("size-3.5 shrink-0 transition-transform", detailsOpen && "rotate-180")}
                aria-hidden
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-1.5">
              <NightList coverage={coverage} />
            </CollapsibleContent>
          </Collapsible>
        ) : (
          <>
            <span className="text-muted-foreground text-xs font-medium">
              Each {coverage.dateWord}
            </span>
            <NightList coverage={coverage} />
          </>
        )}
      </div>

      {coverage.windowHint ? (
        <p className="text-muted-foreground text-xs leading-snug">{coverage.windowHint}</p>
      ) : null}
    </div>
  );
}
